import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import type { NavDataType } from "@/constants/common";
import {
  NAV_BAR_ACCENT_PILL,
  NAV_BAR_COLORS,
  NAV_BAR_LABELS,
  NAV_BAR_LAYOUT,
  NAV_BAR_POPUP_IDS,
  NAV_BAR_TYPOGRAPHY,
  PROFILE_HELP_VALUE,
  PROFILE_LOG_OUT_VALUE,
} from "../nav-bar-constant";
import type { NavBarLocation, NavBarUser } from "../nav-bar-types";

export interface NavBarProfileMenuProps {
  user: NavBarUser;
  profileData: NavDataType[];
  /** Provider-only rows: the group and location the session is pinned to. */
  providerGroup?: string;
  loggedLocation?: NavBarLocation;
  onProfileClick: (item: NavDataType) => void;
}

const NavBarProfileMenu = ({
  user,
  profileData,
  providerGroup,
  loggedLocation,
  onProfileClick,
}: NavBarProfileMenuProps) => {
  const showSessionFields = !!providerGroup || !!loggedLocation;

  return (
    <PopupState variant="popover" popupId={NAV_BAR_POPUP_IDS.PROFILE_MENU}>
      {(popupState) => (
        <>
          <ButtonBase
            disableRipple
            sx={{ gap: 1, p: 0 }}
            aria-label={user.fullName || user.shortName}
            {...bindTrigger(popupState)}
          >
            <Avatar
              src={user.avatarUrl}
              alt=""
              sx={{
                width: NAV_BAR_LAYOUT.AVATAR_SIZE,
                height: NAV_BAR_LAYOUT.AVATAR_SIZE,
                fontSize: NAV_BAR_LAYOUT.AVATAR_FONT_SIZE,
                bgcolor: NAV_BAR_COLORS.AVATAR_BG,
                color: NAV_BAR_COLORS.AVATAR_TEXT,
              }}
            >
              {user.initials}
            </Avatar>
            {!!user.shortName && (
              <Text
                fontSize={NAV_BAR_TYPOGRAPHY.LABEL_FONT_SIZE}
                fontWeight={NAV_BAR_TYPOGRAPHY.LABEL_FONT_WEIGHT}
                c={NAV_BAR_COLORS.ON_BAR}
                noWrap
              >
                {user.shortName}
              </Text>
            )}
          </ButtonBase>

          <Menu {...bindMenu(popupState)}>
            <Flex
              mt={NAV_BAR_LAYOUT.PROFILE_MENU_TOP_GAP}
              boxSizing="border-box"
              width={NAV_BAR_LAYOUT.PROFILE_MENU_WIDTH}
              height={
                showSessionFields
                  ? NAV_BAR_LAYOUT.PROFILE_MENU_HEIGHT
                  : NAV_BAR_LAYOUT.PROFILE_MENU_HEIGHT_COMPACT
              }
              justifyContent="space-between"
              flexDirection="column"
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap={1}
              >
                <Avatar
                  src={user.avatarUrl}
                  alt=""
                  sx={{
                    width: NAV_BAR_LAYOUT.AVATAR_LARGE_SIZE,
                    height: NAV_BAR_LAYOUT.AVATAR_LARGE_SIZE,
                    fontSize: NAV_BAR_LAYOUT.AVATAR_LARGE_FONT_SIZE,
                    bgcolor: NAV_BAR_COLORS.AVATAR_BG,
                    color: NAV_BAR_COLORS.AVATAR_TEXT,
                  }}
                >
                  {user.initials}
                </Avatar>
                <Text fontWeight="bold" variant="h4">
                  {user.fullName}
                </Text>
                {!!user.role && (
                  <Chip
                    size="small"
                    label={user.role}
                    sx={NAV_BAR_ACCENT_PILL}
                  />
                )}
              </Flex>

              {showSessionFields && (
                <Flex
                  gap={2}
                  flexDirection="column"
                  p={3}
                  boxSizing="border-box"
                >
                  {!!providerGroup && (
                    <CustomSelect
                      readOnly
                      label={NAV_BAR_LABELS.PROVIDER_GROUP_FIELD}
                      value={providerGroup}
                      options={[{ value: providerGroup, label: providerGroup }]}
                    />
                  )}
                  {!!loggedLocation && (
                    <CustomSelect
                      readOnly
                      label={NAV_BAR_LABELS.LOGGED_LOCATION_FIELD}
                      value={loggedLocation.id}
                      options={[
                        {
                          value: loggedLocation.id,
                          label: loggedLocation.label,
                        },
                      ]}
                    />
                  )}
                </Flex>
              )}

              <Box>
                <Divider sx={{ marginY: "5px" }} />
                {profileData.map((item) => (
                  <MenuItem
                    key={item.value}
                    disabled={item.value === PROFILE_HELP_VALUE}
                    sx={{ marginTop: "8px" }}
                    onClick={() => {
                      if (item.value !== PROFILE_LOG_OUT_VALUE)
                        popupState.close();
                      onProfileClick(item);
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon>
                        <item.icon fontSize="small" />
                      </ListItemIcon>
                    )}
                    <Text c={NAV_BAR_COLORS.MENU_ITEM_TEXT}>{item.label}</Text>
                  </MenuItem>
                ))}
              </Box>
            </Flex>
          </Menu>
        </>
      )}
    </PopupState>
  );
};

export default NavBarProfileMenu;
